<template>
    <div class="voice-settings">
        <h3 class="settings-title">{{ $t("voiceagent.voiceSettings") }}</h3>

        <!-- Ses Modeli Seçimi -->
        <div class="settings-section">
            <h4 class="section-title">{{ $t("voiceagent.voiceModel") }}</h4>
            <div class="voice-model-options">
                <div
                    v-for="model in voiceModels"
                    :key="model.id"
                    class="voice-model-option"
                    :class="{ selected: localVoiceModel === model.id }"
                    @click="selectVoiceModel(model.id)"
                >
                    <div class="radio-button">
                        <div class="radio-dot" v-if="localVoiceModel === model.id"></div>
                    </div>
                    <div class="model-info">
                        <div class="model-name-row">
                            <span class="model-name">{{ model.name }}</span>
                            <span class="model-price">${{ model.priceUsd.toFixed(2) }}/dk</span>
                        </div>
                        <div class="model-description">{{ model.description }}</div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Ses Sıcaklığı (Temperature) -->
        <div class="settings-section">
            <div class="slider-header">
                <h4 class="section-title">{{ $t("voiceagent.voiceTemperature") }}</h4>
                <span class="value">{{ localVoiceTemperature.toFixed(2) }}</span>
            </div>
            <a-slider v-model="localVoiceTemperature" :min="0" :max="2" :step="0.01" />
            <div class="slider-description">
                {{ $t("voiceagent.voiceTemperatureDescription") }}
            </div>
        </div>

        <!-- Ses Hızı (Speed) -->
        <div class="settings-section">
            <div class="slider-header">
                <h4 class="section-title">{{ $t("voiceagent.voiceSpeed") }}</h4>
                <span class="value">{{ localVoiceSpeed.toFixed(2) }}</span>
            </div>
            <a-slider v-model="localVoiceSpeed" :min="0.5" :max="2" :step="0.01" />
            <div class="slider-description">
                {{ $t("voiceagent.voiceSpeedDescription") }}
            </div>
        </div>

        <!-- Ses Seviyesi (Volume) -->
        <div class="settings-section">
            <div class="slider-header">
                <h4 class="section-title">{{ $t("voiceagent.voiceVolume") }}</h4>
                <span class="value">{{ localVoiceVolume.toFixed(2) }}</span>
            </div>
            <a-slider v-model="localVoiceVolume" :min="0" :max="2" :step="0.01" />
            <div class="slider-description">
                {{ $t("voiceagent.voiceVolumeDescription") }}
            </div>
        </div>

        <!-- Butonlar -->
        <div class="action-buttons">
            <a-button @click="resetSettings">{{ $t("voiceagent.cancel") }}</a-button>
            <PermissionController :permissions="['voice-agent.update']">
                <a-button type="primary" @click="saveSettings">{{ $t("voiceagent.save") }}</a-button>
            </PermissionController>
        </div>
    </div>
</template>

<script>
import { getElevenLabsVoiceModelUsdPerMinute } from "../../../../../utils/voiceAgentCustomerLlmRate";

export default {
    name: "VoiceSettings",
    props: {
        voiceModel: {
            type: String,
            default: "eleven_multilingual_v2",
        },
        voiceTemperature: {
            type: Number,
            default: 1.0,
        },
        voiceSpeed: {
            type: Number,
            default: 1.0,
        },
        voiceVolume: {
            type: Number,
            default: 1.0,
        },
    },
    data() {
        return {
            localVoiceModel: this.voiceModel,
            localVoiceTemperature: this.voiceTemperature,
            localVoiceSpeed: this.voiceSpeed,
            localVoiceVolume: this.voiceVolume,
            originalValues: {
                voiceModel: this.voiceModel,
                voiceTemperature: this.voiceTemperature,
                voiceSpeed: this.voiceSpeed,
                voiceVolume: this.voiceVolume,
            },
            voiceModels: [
                {
                    id: "eleven_multilingual_v2",
                    name: "Auto(Elevenlabs Multilingual v2)",
                    description: "Multilingual, fast, high quality",
                    priceUsd: getElevenLabsVoiceModelUsdPerMinute("eleven_multilingual_v2"),
                },
                {
                    id: "eleven_turbo_v2_5",
                    name: "Elevenlabs Turbo V2.5",
                    description: "Multilingual, fast, high quality",
                    priceUsd: getElevenLabsVoiceModelUsdPerMinute("eleven_turbo_v2_5"),
                },
                {
                    id: "eleven_flash_v2_5",
                    name: "Elevenlabs Flash V2.5",
                    description: "Multilingual, fastest, medium quality",
                    priceUsd: getElevenLabsVoiceModelUsdPerMinute("eleven_flash_v2_5"),
                },
                {
                    id: "eleven_multilingual_v2",
                    name: "Elevenlabs Multilingual v2",
                    description: "Multilingual, slow, highest quality",
                    priceUsd: getElevenLabsVoiceModelUsdPerMinute("eleven_multilingual_v2"),
                },
            ],
        };
    },
    watch: {
        voiceModel(newValue) {
            this.localVoiceModel = newValue;
            this.originalValues.voiceModel = newValue;
        },
        voiceTemperature(newValue) {
            this.localVoiceTemperature = newValue;
            this.originalValues.voiceTemperature = newValue;
        },
        voiceSpeed(newValue) {
            this.localVoiceSpeed = newValue;
            this.originalValues.voiceSpeed = newValue;
        },
        voiceVolume(newValue) {
            this.localVoiceVolume = newValue;
            this.originalValues.voiceVolume = newValue;
        },
    },
    methods: {
        selectVoiceModel(modelId) {
            this.localVoiceModel = modelId;
        },
        resetSettings() {
            // Değerleri orijinal haline geri döndür
            this.localVoiceModel = this.originalValues.voiceModel;
            this.localVoiceTemperature = this.originalValues.voiceTemperature;
            this.localVoiceSpeed = this.originalValues.voiceSpeed;
            this.localVoiceVolume = this.originalValues.voiceVolume;

            // Popover'ı kapat
            this.$emit("close");
        },
        saveSettings() {
            // Değerleri güncelle
            this.$emit("update", {
                voiceModel: this.localVoiceModel,
                voiceTemperature: this.localVoiceTemperature,
                voiceSpeed: this.localVoiceSpeed,
                voiceVolume: this.localVoiceVolume,
            });

            // Orijinal değerleri güncelle
            this.originalValues = {
                voiceModel: this.localVoiceModel,
                voiceTemperature: this.localVoiceTemperature,
                voiceSpeed: this.localVoiceSpeed,
                voiceVolume: this.localVoiceVolume,
            };

            // Popover'ı kapat
            this.$emit("close");
        },
    },
};
</script>

<style scoped lang="scss">
.voice-settings {
    padding: 15px;

    .settings-title {
        margin: 0 0 15px 0;
        font-size: 16px;
        font-weight: 500;
    }

    .settings-section {
        margin-bottom: 20px;

        .section-title {
            margin: 0 0 10px 0;
            font-size: 14px;
            font-weight: 500;
        }

        .slider-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 10px;

            .section-title {
                margin: 0;
            }

            .value {
                font-weight: 500;
                color: #1890ff;
            }
        }

        .slider-description {
            margin-top: 5px;
            font-size: 12px;
            color: #8c8c8c;
        }

        .voice-model-options {
            display: flex;
            flex-direction: column;
            gap: 10px;

            .voice-model-option {
                display: flex;
                align-items: flex-start;
                padding: 10px;
                border: 1px solid #d9d9d9;
                border-radius: 4px;
                cursor: pointer;
                transition: all 0.2s;

                &:hover {
                    border-color: #1890ff;
                }

                &.selected {
                    border-color: #1890ff;
                    background-color: #e6f7ff;
                }

                .radio-button {
                    width: 16px;
                    height: 16px;
                    border: 1px solid #d9d9d9;
                    border-radius: 50%;
                    margin-right: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin-top: 2px;

                    .radio-dot {
                        width: 8px;
                        height: 8px;
                        background-color: #1890ff;
                        border-radius: 50%;
                    }
                }

                .model-info {
                    flex: 1;

                    .model-name-row {
                        display: flex;
                        align-items: flex-start;
                        justify-content: space-between;
                        gap: 8px;
                        margin-bottom: 3px;
                    }

                    .model-name {
                        font-weight: 500;
                    }

                    .model-price {
                        flex-shrink: 0;
                        font-size: 13px;
                        font-weight: 600;
                        color: #1890ff;
                    }

                    .model-description {
                        font-size: 12px;
                        color: #8c8c8c;
                    }
                }
            }
        }
    }

    .action-buttons {
        display: flex;
        justify-content: flex-end;
        gap: 8px;
        margin-top: 15px;
    }
}
</style>
